import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
// tslint:disable:no-submodule-imports
import uuid from 'uuid/v4';
import { getCountries } from 'react-phone-number-input/input';
// tslint:enable:no-submodule-imports
import { useIntl } from 'react-intl';
import {
  Field,
  useFormikContext,
  getIn,
  FormikContextType,
  FormikTouched,
  FormikValues,
  FormikErrors,
} from 'formik';

// Components
import { InputGroup } from '../InputGroup';
import CountrySelectPopout from '../PhoneInput/CountrySelect/CountrySelectPopout';
import InternationalIcon from '../../../assets/images/international-icon.inline.svg';

// Config
import config from '../../../config.json';

// Hooks
import useGenerateFieldMeta from '../../../hooks/Formik/useGenerateFieldMeta';

// Messages
import translations from './messages';

// tslint:disable:no-submodule-imports
import de from 'react-phone-number-input/locale/de.json';
import el from 'react-phone-number-input/locale/el.json';
import en from 'react-phone-number-input/locale/en.json';
import es from 'react-phone-number-input/locale/es.json';
import fi from 'react-phone-number-input/locale/fi.json';
import fr from 'react-phone-number-input/locale/fr.json';
import it from 'react-phone-number-input/locale/it.json';
import nb from 'react-phone-number-input/locale/nb.json';
import pl from 'react-phone-number-input/locale/pl.json';
import pt from 'react-phone-number-input/locale/pt.json';
import ru from 'react-phone-number-input/locale/ru.json';
import sv from 'react-phone-number-input/locale/sv.json';
import vi from 'react-phone-number-input/locale/vi.json';
// tslint:enable:no-submodule-imports

export function getSelectedOption(
  options: { key: string; value: string; label: string; divider?: string }[],
  value: string
) {
  for (const option of options) {
    if (!option.divider && option.value === value) {
      return option;
    }
  }
}

const localeToTranslationMap = {
  de,
  el,
  en,
  es,
  fi,
  fr,
  it,
  nb,
  pl,
  pt,
  ru,
  sv,
  vi,
};

const generateOption = (locale: string, country: string) => {
  const translatedLabel = localeToTranslationMap[locale] || en;
  return {
    key: country,
    value: country,
    label: `${translatedLabel[country]}`,
  };
};

const mapCountriesToOptions = (countries: string[], locale: string) => {
  return countries.map((country: string) => generateOption(locale, country));
};

enum CountryToValueMap {
  NL = 'bsn',
  GB = 'nhs',
  FR = 'insee',
  ES = 'dni',
  IT = 'fiscal_code',
  BE = 'be',
  PL = 'pesel',
  AT = 'ssn',
  IE = 'personal_public_service_number',
}

const typeFieldName = 'national_personal_number_type';

const errorMessage = (
  errors: FormikErrors<FormikValues>,
  touched: FormikTouched<FormikValues>,
  name: string
) => {
  const error = getIn(errors, name);
  const touch = getIn(touched, name);
  return touch && error ? error : null;
};

type NationalPersonNumberFieldProps = {
  name: string;
  className?: string;
  headerLabel?: string | {};
  placeholder: { id: string; defaultMessage: string };
  label: { id: string; defaultMessage: string };
  wrapperClassName?: string;
  id?: string;
};

const NationalPersonNumberField = ({
  className,
  headerLabel,
  placeholder,
  label,
  name,
  wrapperClassName,
  ...rest
}: NationalPersonNumberFieldProps) => {
  const {
    values,
    errors,
    touched,
    initialValues,
    setFieldValue,
  }: FormikContextType<FormikValues> = useFormikContext();
  const id = useMemo(() => rest.id || `input-${uuid()}`, [rest.id]);
  const initialCountry = useRef(
    initialValues?.countryOfBirth || config?.country?.toUpperCase()
  );
  const locale = config.locale;
  const { formatMessage } = useIntl();

  const typeMeta = useGenerateFieldMeta(typeFieldName);
  const numberMeta = useGenerateFieldMeta(name);
  const nationalNumberTypeError = errorMessage(errors, touched, typeFieldName);
  const numberError = errorMessage(errors, touched, name);

  const options = locale
    ? mapCountriesToOptions(getCountries(), locale)
    : mapCountriesToOptions(getCountries(), 'en');

  useEffect(() => {
    if (initialCountry.current) {
      const value = CountryToValueMap[initialCountry.current] || 'other';
      setFieldValue('national_personal_number_type', value);
    }
  }, [initialCountry, setFieldValue]);

  const selectedOption = useMemo(() => {
    return getSelectedOption(options, initialCountry.current);
  }, [options, initialCountry]);

  const onCountryChange = useCallback(
    valueObject => {
      const value = CountryToValueMap[valueObject.value] || 'other';
      setFieldValue('national_personal_number_type', value);

      initialCountry.current = valueObject.value;
    },
    [setFieldValue]
  );

  const transformString = (item: string) => {
    if (item === 'other' || item === 'personal_public_service_number') {
      return item.charAt(0).toUpperCase() + item.slice(1);
    }
    return item?.toUpperCase();
  };

  const newLabel = `${formatMessage(
    translations.nationalIdNumberExtra
  )} (${transformString(
    values?.national_personal_number_type ||
      CountryToValueMap[initialCountry.current]
  )})`;

  return (
    <InputGroup
      id={id}
      label={newLabel || label}
      headerLabel={headerLabel}
      className={className}
      meta={numberMeta || typeMeta}
      wrapperClassName={wrapperClassName}
      errorMessage={numberError || nationalNumberTypeError}
    >
      <div
        className={clsx(
          'flex',
          'form-input',
          'w-full',
          'sm:max-w-315',
          'placeholder-gray-300',
          {
            'form-input-error': nationalNumberTypeError || numberError,
          },
          className
        )}
      >
        <Field
          name={typeFieldName}
          component={CountrySelectPopout}
          onChange={onCountryChange}
          options={options}
          value={initialCountry.current}
          className={className}
          selectedOption={selectedOption}
          internationalIcon={InternationalIcon}
        />
        <Field
          className={clsx(
            'w-full',
            'placeholder-gray-300',
            'focus:outline-none',
            className
          )}
          name={name} // national_personal_number
          placeholder={
            placeholder?.id ? formatMessage(placeholder) : placeholder
          }
        />
      </div>
    </InputGroup>
  );
};

export default NationalPersonNumberField;
